import { apiWithFetchAdapter } from 'api'
import {
  AnalysisResponse,
  AnalysisStatus,
} from 'pages/Shifts/ShiftDetail/components/ShiftIntelligenceWidget/types'

const eventTypes = ['status', 'delta', 'done', 'error'] as const
type EventType = (typeof eventTypes)[number]

function createAsyncIteratorFromStream(
  stream: ReadableStream<Uint8Array>
): AsyncIterableIterator<AnalysisResponse> {
  const reader = stream.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  const iterator: AsyncIterableIterator<AnalysisResponse> = {
    async next(): Promise<IteratorResult<AnalysisResponse>> {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Do we have a full SSE message (terminated by blank line)?
        const idx = buffer.indexOf('\n\n')
        if (idx !== -1) {
          const msg = buffer.slice(0, idx)
          buffer = buffer.slice(idx + 2)

          let eventType: EventType = 'delta'
          const dataLines: string[] = []

          for (const line of msg.split(/\r?\n/)) {
            if (line.startsWith('event:')) {
              const foundEventType = line.slice(6).trim()
              if (eventTypes.includes(foundEventType as EventType)) {
                eventType = foundEventType as EventType
              } else {
                console.warn(`Unknown event type detected: ${foundEventType}`)
              }
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5))
            }
          }

          const dataStr = dataLines.join('\n')
          if (eventType === 'status') {
            return {
              done: false,
              value: JSON.parse(dataStr) as AnalysisStatus,
            }
          }

          if (eventType === 'delta') {
            try {
              const payload = JSON.parse(dataStr)
              return { done: false, value: payload }
            } catch (err) {
              console.error('Failed to parse delta:', dataStr)
              throw new Error('Invalid delta payload')
            }
          }

          if (eventType === 'done') {
            return { done: true, value: '' }
          }

          if (eventType === 'error') {
            let errorMessage = 'Unknown error'
            try {
              const parsed = JSON.parse(dataStr)
              errorMessage = parsed.message || JSON.stringify(parsed)
            } catch {
              errorMessage = dataStr
            }
            throw new Error(errorMessage)
          }

          // Skip other events
          continue
        }

        const { value, done } = await reader.read()
        if (done) {
          return { done: true, value: undefined }
        }
        buffer += decoder.decode(value, { stream: true })
      }
    },

    async return(): Promise<IteratorResult<AnalysisResponse>> {
      reader.cancel()
      return { done: true, value: undefined }
    },

    [Symbol.asyncIterator]() {
      return this
    },
  }

  return iterator
}

export async function getShiftAnalysis(request: {
  shiftId: number
}): Promise<AsyncIterator<AnalysisResponse> & AsyncIterable<AnalysisResponse>> {
  const response = await apiWithFetchAdapter.post<ReadableStream<Uint8Array>>(
    '/agent/ops',
    { shift_id: request.shiftId },
    {
      headers: { Accept: 'text/event-stream' },
      responseType: 'stream',
      adapter: 'fetch',
    }
  )

  const stream = response.data
  return createAsyncIteratorFromStream(stream)
}
